import {
  GLOBAL_COUNTRY_OPTIONS,
  GLOBAL_MORE_LANGUAGE_OPTIONS,
  GLOBAL_PREFERS_OPTIONS,
  GLOBAL_PREFIX_OPTIONS,
  GLOBAL_PRONOUN_OPTIONS,
  GLOBAL_RACE_OPTIONS,
  GLOBAL_SUFFIX_OPTIONS,
} from '@chiroup/core/constants/globals';
import { Region } from '@chiroup/core/types/Condition.type';
import type { TreatmentTypeUnion } from '@chiroup/core/types/Treatment.type';

export const LIST_LIMIT = 30;

export enum Regions {
  'C' = 'C',
  'S' = 'S',
  'E' = 'E',
  'H/W' = 'H/W',
  'T' = 'T',
  'L' = 'L',
  'H' = 'H',
  'K' = 'K',
  'F/A' = 'F/A',
  'O' = 'O',
  'HEAD' = 'HEAD',
  'F/B' = 'F/B',
}

export enum JointRegions {
  SPINE = 'SPINE',
  RIBS = 'RIBS',
  'S' = 'S',
  'E' = 'E',
  'H/W' = 'H/W',
  'H' = 'H',
  'K' = 'K',
  'F/A' = 'F/A',
  'O' = 'O',
  'HEAD' = 'HEAD',
  'F/B' = 'F/B',
}

export const JOINT_REGION_TO_REGION: {
  [key in Region]: JointRegions;
} = {
  [Regions.C]: JointRegions.SPINE,
  [Regions.S]: JointRegions.S,
  [Regions.E]: JointRegions.E,
  [Regions.HEAD]: JointRegions.HEAD,
  [Regions['H/W']]: JointRegions['H/W'],
  [Regions.T]: JointRegions.SPINE,
  [Regions.L]: JointRegions.SPINE,
  [Regions.H]: JointRegions.H,
  [Regions.K]: JointRegions.K,
  [Regions['F/A']]: JointRegions['F/A'],
  [Regions.O]: JointRegions.O,
  [Regions['F/B']]: JointRegions['F/B'],
};

// Spine ordering and display mapping used across plan detail and SOAP rendering
const SPINE_ORDER = [
  'C0',
  'C1',
  'C2',
  'C3',
  'C4',
  'C5',
  'C6',
  'C7',
  'T1',
  'T2',
  'T3',
  'T4',
  'T5',
  'T6',
  'T7',
  'T8',
  'T9',
  'T10',
  'T11',
  'T12',
  '1st costovertebral',
  '2nd costovertebral',
  '3rd costovertebral',
  '4th costovertebral',
  '5th costovertebral',
  '6th costovertebral',
  '7th costovertebral',
  '8th costovertebral',
  '9th costovertebral',
  '10th costovertebral',
  '11th costovertebral',
  '12th costovertebral',
  'L1',
  'L2',
  'L3',
  'L4',
  'L5',
  'PL',
  'PR',
  'S',
  'C',
] as const;

const SPINE_SORT_WEIGHT: { [key: string]: number } = SPINE_ORDER.reduce(
  (map, key, index) => {
    map[key] = index;

    return map;
  },
  {} as { [key: string]: number }
);

const SPINE_DISPLAY_NAME: { [key: string]: string } = {
  C0: 'Occiput',
  PL: 'Left Ilium',
  PR: 'Right Ilium',
  S: 'SI Joint',
  C: 'Coccyx',
};

export const getSpineSortWeight = (selection: string) =>
  Object.prototype.hasOwnProperty.call(SPINE_SORT_WEIGHT, selection)
    ? SPINE_SORT_WEIGHT[selection]
    : Number.MAX_SAFE_INTEGER;

export const getSpineDisplayName = (selection: string) =>
  SPINE_DISPLAY_NAME?.[selection] || selection;

export const INSURANCE_TYPE_CODES = [
  {
    value: '12',
    text: 'Medicare Secondary, Working Aged Beneficiary or Spouse with Employer Group Health Plan',
  },
  {
    value: '13',
    text: 'Medicare Secondary, End-Stage Renal Disease Beneficiary in the Mandated Coordination Period with an Employer’s Group Health Plan',
  },
  {
    value: '14',
    text: 'Medicare Secondary, No-fault insurance is Primary',
  },
  { value: '15', text: 'Medicare Secondary, Workers Comp' },
  {
    value: '16',
    text: 'Medicare Secondary, Public Health Service or Other Public Agency',
  },
  { value: '41', text: 'Medicare Secondary, Black Lung' },
  { value: '42', text: 'Medicare Secondary, Veterans Administration' },
  {
    value: '43',
    text: 'Medicare Secondary, Disabled Beneficiary Under Age 65 with Large Group Health Plan',
  },
  {
    value: '47',
    text: 'Medicare Secondary, Other Liability Insurance is Primary',
  },
];
export const DEGREES = [
  { value: '', text: '' },
  { value: 'ARNP', text: 'ARNP' },
  { value: 'APRN', text: 'APRN' },
  { value: 'NP', text: 'NP' },
  { value: 'DC', text: 'DC' },
  { value: 'DDS', text: 'DDS' },
  { value: 'DMD', text: 'DMD' },
  { value: 'DNP', text: 'DNP' },
  { value: 'DO', text: 'DO' },
  { value: 'DPM', text: 'DPM' },
  { value: 'MD', text: 'MD' },
  { value: 'ND', text: 'ND' },
  { value: 'PA', text: 'PA' },
  { value: 'PT', text: 'PT' },
  { value: 'Other', text: 'Other' },
];

export const MEDICAL_SPECIALTIES = [
  { value: 'Chiropractic', text: 'Chiropractic' },
  { value: 'Family/Internist', text: 'Family/Internist' },
  { value: 'Neurology', text: 'Neurology' },
  { value: 'Neurosurgery', text: 'Neurosurgery' },
  { value: 'Ob/Gyn', text: 'Ob/Gyn' },
  { value: 'Orthopedic Surgery', text: 'Orthopedic Surgery' },
  { value: 'Pain Management', text: 'Pain Management' },
  { value: 'Pediatrics', text: 'Pediatrics' },
  { value: 'Physiatry', text: 'Physiatry' },
  { value: 'Rheumatology', text: 'Rheumatology' },
  { value: 'Sports Medicine', text: 'Sports Medicine' },
  { value: 'Other', text: 'Other' },
];

export const REGION_OPTIONS: { value: Regions; text: string }[] = [
  { value: Regions.C, text: 'Cervical' },
  { value: Regions.S, text: 'Shoulder' },
  { value: Regions.E, text: 'Elbow' },
  { value: Regions['H/W'], text: 'Hand and Wrist' },
  { value: Regions.T, text: 'Thoracic' },
  { value: Regions.L, text: 'Lumbosacral' },
  { value: Regions.H, text: 'Hip' },
  { value: Regions.K, text: 'Knee' },
  { value: Regions['F/A'], text: 'Foot and Ankle' },
  { value: Regions.HEAD, text: 'Head' },
  { value: Regions['F/B'], text: 'Full Body' },
  { value: Regions.O, text: 'Other' },
];
export const ROM_REGION_OPTIONS: { [key in string]: boolean } = {
  [Regions.C]: true,
  [Regions.S]: true,

  [Regions.E]: true,
  [Regions['H/W']]: true,
  [Regions.T]: true,
  [Regions.L]: true,
  [Regions.H]: true,
  [Regions.K]: true,
  [Regions['F/A']]: true,
};

// Shared ROM tests data for all regions - used by both legacy and unified RangeOfMotion components
export const ROM_TESTS_PER_REGION: {
  [key in Region]?: {
    name: string;
    normal: number;
    min: number;
    max: number;
    value: string;
  }[];
} = {
  [Regions.C]: [
    { value: 'flexion', name: 'Flexion', normal: 50, min: 0, max: 60 },
    { value: 'extension', name: 'Extension', normal: 60, min: 0, max: 75 },
    {
      value: 'leftRotation',
      name: 'Left Rotation',
      normal: 80,
      min: 0,
      max: 100,
    },
    {
      value: 'rightRotation',
      name: 'Right Rotation',
      normal: 80,
      min: 0,
      max: 100,
    },
    {
      value: 'leftLateralFlexion',
      name: 'Left Lateral Flexion',
      normal: 45,
      min: 0,
      max: 55,
    },
    {
      value: 'rightLateralFlexion',
      name: 'Right Lateral Flexion',
      normal: 45,
      min: 0,
      max: 55,
    },
  ],
  [Regions.S]: [
    {
      value: 'elevatedAbduction',
      name: 'Elevated Abduction',
      normal: 150,
      min: 0,
      max: 190,
    },
    {
      value: 'elevatedForwardFlexion',
      name: 'Elevated Forward Flexion',
      normal: 180,
      min: 0,
      max: 225,
    },
    {
      value: 'externalRotation',
      name: 'External Rotation',
      normal: 90,
      min: 0,
      max: 110,
    },
    {
      value: 'internalRotation',
      name: 'Internal Rotation',
      normal: 80,
      min: 0,
      max: 100,
    },
    { value: 'extension', name: 'Extension', normal: 50, min: 0, max: 60 },
    { value: 'adduction', name: 'Adduction', normal: 0, min: -90, max: 20 },
  ],
  [Regions.E]: [
    { value: 'flexion', name: 'Flexion', normal: 150, min: 0, max: 190 },
    { value: 'extension', name: 'Extension', normal: 0, min: -90, max: 20 },
    { value: 'pronation', name: 'Pronation', normal: 90, min: 0, max: 110 },
    { value: 'supination', name: 'Supination', normal: 90, min: 0, max: 110 },
  ],
  [Regions['H/W']]: [
    {
      value: 'forearmPronation',
      name: 'Forearm Pronation',
      normal: 90,
      min: 0,
      max: 110,
    },
    {
      value: 'forearmSupination',
      name: 'Forearm Supination',
      normal: 90,
      min: 0,
      max: 110,
    },
    {
      value: 'wristFlexion',
      name: 'Wrist Flexion',
      normal: 90,
      min: 0,
      max: 110,
    },
    {
      value: 'wristExtension',
      name: 'Wrist Extension',
      normal: 80,
      min: 0,
      max: 100,
    },
    {
      value: 'wristRadialDeviation',
      name: 'Wrist Radial Deviation',
      normal: 20,
      min: 0,
      max: 25,
    },
    {
      value: 'wristUlnarDeviation',
      name: 'Wrist Ulnar Deviation',
      normal: 45,
      min: 0,
      max: 55,
    },
  ],
  [Regions.T]: [
    { value: 'flexion', name: 'Flexion', normal: 30, min: 0, max: 40 },
    { value: 'extension', name: 'Extension', normal: 30, min: 0, max: 40 },
    {
      value: 'leftLateralFlexion',
      name: 'Left lateral Flexion',
      normal: 30,
      min: 0,
      max: 40,
    },
    {
      value: 'rightLateralFlexion',
      name: 'Right Lateral Flexion',
      normal: 30,
      min: 0,
      max: 40,
    },
    {
      value: 'leftRotation',
      name: 'Left Rotation',
      normal: 40,
      min: 0,
      max: 50,
    },
    {
      value: 'rightRotation',
      name: 'Right Rotation',
      normal: 40,
      min: 0,
      max: 50,
    },
  ],
  [Regions.L]: [
    { value: 'flexion', name: 'Flexion', normal: 60, min: 0, max: 75 },
    { value: 'extension', name: 'Extension', normal: 30, min: 0, max: 40 },
    {
      value: 'leftLateralFlexion',
      name: 'Left Lateral Flexion',
      normal: 25,
      min: 0,
      max: 50,
    },
    {
      value: 'rightLateralFlexion',
      name: 'Right Lateral Flexion',
      normal: 25,
      min: 0,
      max: 50,
    },
    {
      value: 'leftRotation',
      name: 'Left Rotation',
      normal: 30,
      min: 0,
      max: 50,
    },
    {
      value: 'rightRotation',
      name: 'Right Rotation',
      normal: 30,
      min: 0,
      max: 50,
    },
  ],
  [Regions.H]: [
    { value: 'flexion', name: 'Flexion', normal: 125, min: 0, max: 155 },
    { value: 'extension', name: 'Extension', normal: 15, min: 0, max: 20 },
    { value: 'abduction', name: 'Abduction', normal: 45, min: 0, max: 55 },
    { value: 'adduction', name: 'Adduction', normal: 20, min: 0, max: 25 },
    {
      value: 'externalRotation',
      name: 'External Rotation',
      normal: 45,
      min: 0,
      max: 55,
    },
    {
      value: 'internalRotation',
      name: 'Internal Rotation',
      normal: 45,
      min: 0,
      max: 55,
    },
  ],
  [Regions.K]: [
    { value: 'flexion', name: 'Flexion', normal: 140, min: 0, max: 175 },
    { value: 'extension', name: 'Extension', normal: 0, min: -90, max: 20 },
  ],
  [Regions['F/A']]: [
    {
      value: 'ankleDorsiflexion',
      name: 'Ankle Dorsiflexion',
      normal: 20,
      min: 0,
      max: 25,
    },
    {
      value: 'anklePlantarflexion',
      name: 'Ankle Plantarflexion',
      normal: 45,
      min: 0,
      max: 55,
    },
    {
      value: 'footInversion',
      name: 'Foot Inversion',
      normal: 35,
      min: 0,
      max: 45,
    },
    {
      value: 'footEversion',
      name: 'Foot Eversion',
      normal: 25,
      min: 0,
      max: 30,
    },
  ],
};

// ==================== MYOFASCIAL SHARED CONSTANTS ====================
// Pelvic floor muscles that should not be auto selected - shared between legacy and unified Myofascial components
export const MYOFASCIAL_MUSCLES_NOT_AUTO_SELECT: { [key: string]: string } = {
  13: 'Coccygeus',
  47: 'Levator ani',
  55: 'Obturator internus',
  140: 'Pubovaginalis',
  141: 'Puborectalis',
  142: 'Iliococcygeus',
  127: 'Pelvic floor muscles',
};

// ==================== NEUROLOGICAL SHARED CONSTANTS ====================
// Dropdown options for spinal neurological tests - shared between legacy and unified Neurological components
export const NEUROLOGICAL_TD_OPTIONS = {
  sensory: [
    { text: 'Intact' },
    { text: 'Normal' },
    { text: 'Impaired' },
    { text: 'Absent' },
    { text: 'Sharp/dull intact' },
    { text: 'Sharp/dull impaired' },
    { text: 'Sharp/dull absent' },
    { text: 'Light touch intact' },
    { text: 'Light touch impaired' },
    { text: 'Light touch absent' },
    { text: 'Not assessed' },
  ],
  motor: [
    { text: 'Grade 0: Total paralysis' },
    { text: 'Grade 1: Palpable or visible contraction' },
    { text: 'Grade 2: Full ROM without gravity' },
    { text: 'Grade 3: Full ROM against gravity' },
    { text: 'Grade 4: Decreased strength' },
    { text: 'Grade 5: Normal strength' },
    { text: 'Not assessed' },
  ],
  reflex: [
    { text: '0: Absent' },
    { text: '1+: Sluggish' },
    { text: '2+: Normal' },
    { text: '3+: Brisk' },
    { text: '4+: Sustained clonus' },
    { text: 'Not assessed' },
  ],
};

// Dropdown options for peripheral neurological tests - shared between legacy and unified Neurological components
export const NEUROLOGICAL_PERIPHERAL_TD_OPTIONS = {
  sensation: [
    { text: 'Hypersensitive' },
    { text: 'Intact' },
    { text: 'Diminished' },
    { text: 'Absent' },
    { text: 'Other' },
    { text: 'Not assessed' },
  ],
  'muscle strength': [
    { text: 'Normal' },
    { text: 'Diminished' },
    { text: 'Absent' },
    { text: 'Other' },
    { text: 'Not assessed' },
  ],
};

// Abbreviations for neurological test results - shared between legacy and unified Neurological components
export const NEUROLOGICAL_ABBREVIATIONS: { [key: string]: string } = {
  'Not Assessed': 'NA',
  'Grade 0: Total paralysis': '0/5',
  'Grade 1: Palpable or visible contraction': '1/5',
  'Grade 2: Full ROM without gravity': '2/5',
  'Grade 3: Full ROM against gravity': '3/5',
  'Grade 4: Decreased strength': '4/5',
  'Grade 5: Normal strength': '5/5',
  '0: Absent': '0/4',
  '1+: Sluggish': '1/4',
  '2+: Normal': '2/4',
  '3+: Brisk': '3/4',
  '4+: Sustained clonus': '4/4',
};

// Column width settings for neurological tests - shared between legacy and unified Neurological components
export const NEUROLOGICAL_WIDE_COLS: { [key: string]: boolean } = {
  sensory: true,
  sensation: true,
  'muscle strength': true,
};

// Tests that are not assessed by default - shared between legacy and unified Neurological components
export const NEUROLOGICAL_NOT_ASSESSED: string[] = ['L5reflex', 'L2sensory', 'L2motor'];

// Tests that don't have dropdown options - shared between legacy and unified Neurological components
export const NEUROLOGICAL_NO_DROPDOWN: string[] = [
  'C8reflex',
  'L2reflex',
  'L3reflex',
  // sensory-only lower peripheral nerves: suppress motor grading
  'LFCNmuscle strength',
  'suralmuscle strength',
  'saphenousmuscle strength',
];

// Column options for spinal neurological tests - shared between legacy and unified Neurological components
export const NEUROLOGICAL_SPINAL_OPTIONS: { text: string; normalDefaultValue: string }[] = [
  { text: 'sensory', normalDefaultValue: 'Intact' },
  { text: 'motor', normalDefaultValue: 'Grade 5: Normal strength' },
  { text: 'reflex', normalDefaultValue: '2+: Normal' },
];

// Column options for peripheral neurological tests - shared between legacy and unified Neurological components
export const NEUROLOGICAL_PERIPHERAL_OPTIONS: { text: string; normalDefaultValue: string }[] = [
  { text: 'sensation', normalDefaultValue: 'Intact' },
  { text: 'muscle strength', normalDefaultValue: 'Normal' },
];

// Row labels for cervical spinal tests - shared between legacy and unified Neurological components
export const NEUROLOGICAL_CERVICAL_ROWS: string[] = ['C5', 'C6', 'C7', 'C8', 'T1'];

// Row labels for lumbar spinal tests - shared between legacy and unified Neurological components
export const NEUROLOGICAL_LUMBAR_ROWS: string[] = ['L2', 'L3', 'L4', 'L5', 'S1'];

// Row labels for upper peripheral tests - shared between legacy and unified Neurological components
export const NEUROLOGICAL_UPPER_ROWS: string[] = ['axillary', 'radial', 'median', 'ulnar', 'MCN'];

// Row labels for lower peripheral tests - shared between legacy and unified Neurological components
export const NEUROLOGICAL_LOWER_ROWS: string[] = [
  'sciatic',
  'femoral',
  'LFCN',
  'obturator',
  'tibial',
  'fibular',
  'sural',
  'saphenous',
];

// Default values for central neurological tests - shared between legacy and unified Neurological components
export const NEUROLOGICAL_CENTRAL_DEFAULTS = {
  mentalStatus: {
    attention: { choice: 'Alert' },
    personPlaceTime: { choice: 'Oriented' },
    speech: { choice: 'Clear and fluent' },
    memoryThoughtProcess: { choice: 'Intact' },
  },
  coordination: {
    fingerToNoseTest: { choice: 'Normal' },
    heelToShinTest: { choice: 'Normal' },
    hautantsManeuver: { choice: 'No sign of drift' },
    rhombergsSign: { choice: 'Absent' },
    rapidAlternatingFingerMovements: { choice: 'Intact' },
    fineFingerMovements: { choice: 'Normal' },
    stanceBase: { choice: 'Normal' },
    gait: { choice: 'Steady' },
    stepsAndTurning: { choice: 'Normal' },
    heelWalkAndToeWalk: { choice: 'Normal' },
  },
  pathologicReflexes: {
    ankleClonus: { choice: 'Absent' },
    babinskiSign: { choice: 'Absent' },
    hoffmanSign: { choice: 'Absent' },
  },
};

// Default values for cranial neurological tests - shared between legacy and unified Neurological components
export const NEUROLOGICAL_CRANIAL_DEFAULTS = {
  CNI: { choice: 'Smell intact' },
  CNIIIIIIVVI: {
    choice: [
      'Visual acuity intact',
      'Visual fields normal',
      'Pupils equal/round/reactive/ accommodate',
      'Extraocular movements intact',
      'No Nystagmus',
      'No Ptosis',
    ],
  },
  CNV: {
    choice: 'Face sensation intact',
  },
  CNVII: { choice: 'Face motor intact' },
  CNVIII: {
    choice: 'Hearing intact',
  },
  CNIXX: {
    choice: ['Swallow and gag reflex intact', 'Speech intact', 'Voice normal'],
  },
  CNXI: {
    choice: 'Head turn/shoulder shrug intact',
  },
  CNXII: {
    choice: 'Tongue protrusion midline',
  },
};

// ==================== ORTHOPEDIC SHARED CONSTANTS ====================
// Default values for orthopedic evaluation form - shared between legacy and unified Orthopedic components
export const ORTHOPEDIC_DEFAULT_VALUES = {
  name: '',
  regions: [],
  descr: '',
};

export const LANGUAGES = [
  { value: 'en', text: 'English' },
  { value: 'es', text: 'Spanish' },
];

export const MORE_LANGUAGES = GLOBAL_MORE_LANGUAGE_OPTIONS;

export const SESSION_UNITS = [
  { value: '', text: '' },
  { value: 'hour', text: 'hour' },
  { value: 'day', text: 'day' },
  { value: 'week', text: 'week' },
  { value: 'month', text: 'month' },
  { value: 'as needed', text: 'PRN' },
];

export const TREATMENT_CATEGORIES = [
  { value: 'Manipulation', text: 'Manipulation' },
  { value: 'Mobilization', text: 'Mobilization' },
  {
    value: 'DirectionalPreferenceTherapy',
    text: 'Directional preference therapy',
  },
  { value: 'STM', text: 'STM' },
  { value: 'IASTM', text: 'IASTM' },
  { value: 'Neurodynamics', text: 'Neurodynamics' },
  { value: 'Taping', text: 'Taping' },
  { value: 'Other', text: 'Other' },
];

export const EXERCISE_TYPES = [
  { value: 'PROM', text: 'PROM' },
  { value: 'AROM', text: 'AROM' },
  { value: 'Stretch', text: 'Stretch' },
  { value: 'C/R Stretch', text: 'C/R Stretch' },
  { value: 'Stability', text: 'Stability' },
  { value: 'Ball/Band', text: 'Ball/Band' },
  { value: 'Weights', text: 'Weights' },
  { value: 'Functional', text: 'Functional' },
  { value: 'Directional therapy', text: 'Directional therapy' },
  { value: 'Dynamic stabilization', text: 'Dynamic stabilization' },
];

export const treatmentTypes: { [key in TreatmentTypeUnion]: string } = {
  Common: 'common-treatments',
  Clinical: 'treatments',
};

export const PREFERS = GLOBAL_PREFERS_OPTIONS;

export const RACE = GLOBAL_RACE_OPTIONS;

export const PREFIX = GLOBAL_PREFIX_OPTIONS;
export const SUFFIX = GLOBAL_SUFFIX_OPTIONS;

export const TITLES = [
  { value: '', text: 'None' },
  { value: 'Dr', text: 'Dr' },
  { value: 'Mr', text: 'Mr' },
  { value: 'Ms', text: 'Ms' },
  { value: 'Mrs', text: 'Mrs' },
];

export const PRONOUNS = GLOBAL_PRONOUN_OPTIONS;

export const COUNTRIES = GLOBAL_COUNTRY_OPTIONS;

const THEMES = [
  {
    text: 'Default',
    value: 'default-theme',
    primary: '#13547a',
    accent: '#78cac1',
    primaryText: '#ffffff',
    accentText: '#58585A',
  },
  {
    text: 'Green beach',
    value: 'green-beach-theme',
    primary: '#02aab0',
    accent: '#00cdac',
    primaryText: '#ffffff',
    accentText: '#ffffff',
  },
  {
    text: 'Blue',
    value: 'blue-theme',
    primary: '#314755',
    accent: '#26a0da',
    primaryText: '#ffffff',
    accentText: '#58585A',
  },
  {
    text: 'Green',
    value: 'original-green-theme',
    primary: '#76be40',
    accent: '#d2ff9c',
    primaryText: '#ffffff',
    accentText: '#58585A',
  },
  {
    text: 'Orange',
    value: 'orange-theme',
    primary: '#e65c00',
    accent: '#f9d423',
    primaryText: '#ffffff',
    accentText: '#58585A',
  },
  {
    text: 'Purple',
    value: 'purple-theme',
    primary: '#cc2b5e',
    accent: '#753a88',
    primaryText: '#ffffff',
    accentText: '#ffffff',
  },
  {
    text: 'Ultraviolet',
    value: 'ultra-violet-theme',
    primary: '#654ea3',
    accent: '#eaafc8',
    primaryText: '#ffffff',
    accentText: '#58585A',
  },
  {
    text: 'Blue/Orange',
    value: 'blue-orange-theme',
    primary: '#544a7d',
    accent: '#ffd452',
    primaryText: '#ffffff',
    accentText: '#58585A',
  },
  {
    text: 'Red',
    value: 'red-theme',
    primary: '#ed213a',
    accent: '#93291e',
    primaryText: '#ffffff',
    accentText: '#ffffff',
  },
  {
    text: 'Grey',
    value: 'grey-theme',
    primary: '#9a9c9e',
    accent: '#2c3e50',
    primaryText: '#ffffff',
    accentText: '#ffffff',
  },
];

const PLAN_HASH: { [key: string]: string } = {
  premium: 'Premium',
  basic: 'Basic',
  plus: 'Plus',
};
